import { useEffect } from 'react';

import type { AtMentionOption } from '../../types';
import {
  attachMentionBadgeHandlers,
  convertHtmlToMarkdown,
  convertMarkdownToHtml,
  convertMentionsToHTML,
  getIsValueEmpty,
} from '../../utils';

interface UseUpdateValueProps {
  isFocused: boolean;
  spanRef: React.RefObject<HTMLSpanElement | null>;
  value: string;
  placeholder?: string;
  atMentionOptions?: AtMentionOption[];
}

/**
 * @title useUpdateValue
 * @description This hook handles setting the initial value of the input and
 * updating the value when the input is not focused. Also handles placeholder display.
 * @param {UseUpdateValueProps} props - The props for the hook.
 * @param {boolean} props.isFocused - Whether the input is focused.
 * @param {React.RefObject<HTMLSpanElement>} props.spanRef - The ref to the span element.
 * @param {string} props.value - The value to update the input to.
 * @param {string} props.placeholder - Optional placeholder text to show when value is empty.
 */
const useUpdateValue = ({
  isFocused,
  spanRef,
  value,
  placeholder,
  atMentionOptions = [],
}: UseUpdateValueProps) => {
  // Whether the span is currently empty and thus showing the CSS pseudo-element
  // placeholder. This is intentionally narrower than the `isShowingPlaceholder`
  // in useEventHandlers (which gates the visible class / blur logic on
  // rawCharacterCount): here we additionally check the live `textContent` so
  // that Effect 1 still runs its markdown/badge conversion whenever the span
  // actually holds rendered content, even if `value` happens to be empty.
  const isShowingPlaceholder = () => {
    if (!spanRef.current || !placeholder) return false;
    return getIsValueEmpty(value) && !spanRef.current.textContent;
  };

  // Effect 1: Handle focus state changes (convert HTML formatting to markdown when focusing)
  useEffect(() => {
    if (!spanRef.current || !isFocused || isShowingPlaceholder()) return;

    // Convert HTML to markdown for editing, while keeping badges visible
    const currentContent = spanRef.current.innerHTML;

    // Convert HTML formatting to markdown (e.g., <b> to *, <i> to _)
    const markdownContent = convertHtmlToMarkdown(currentContent);

    // Convert mentions back to badges for display (keeps badges visible while editing)
    const withBadges = convertMentionsToHTML(markdownContent, atMentionOptions);
    spanRef.current.innerHTML = withBadges;

    // Reattach badge handlers after recreating them
    attachMentionBadgeHandlers(spanRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]); // Only runs when focus changes to true

  // Effect 2: Handle value updates (external value changes or show placeholder)
  useEffect(() => {
    if (!spanRef.current) return;

    // Leave the span empty when value is empty; the placeholder is shown via
    // the CSS pseudo-element (see .placeholder::before).
    if (getIsValueEmpty(value) && placeholder && !isFocused) {
      // Avoid clobbering the caret with a redundant DOM write when already empty.
      if (spanRef.current.innerHTML !== '') {
        spanRef.current.innerHTML = '';
      }
      return;
    }

    // Only convert markdown to HTML when NOT focused
    // When focused, the user should see the raw markdown
    if (isFocused) return;

    // First convert markdown to HTML, then convert {value} to badges
    const markdownHtml = convertMarkdownToHtml(value);
    const withBadges = convertMentionsToHTML(markdownHtml, atMentionOptions);

    // Update HTML content if it changed
    if (spanRef.current.innerHTML !== withBadges) {
      spanRef.current.innerHTML = withBadges;

      // Attach click handlers to close buttons
      attachMentionBadgeHandlers(spanRef.current);
    }
    // If we include spanRef in the dependency array, it will
    // cause re-renders that bumps the user's cursor to the start.
    // NOTE: atMentionOptions is included in case the question blocks are changed.
    // for example: "answer to #1" might have to update to "answer to #2"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isFocused, placeholder, atMentionOptions]);

  // Effect 3: Set initial content (rendered HTML or placeholder)
  useEffect(() => {
    if (spanRef.current && spanRef.current.innerHTML === '') {
      // Leave the span empty when value is empty: the placeholder is shown via
      // the CSS pseudo-element rather than as editable text content.
      if (getIsValueEmpty(value) && placeholder) {
        return;
      }
      // First convert markdown to HTML, then convert {value} to badges
      const markdownHtml = convertMarkdownToHtml(value);
      const withBadges = convertMentionsToHTML(markdownHtml, atMentionOptions);
      spanRef.current.innerHTML = withBadges;

      // Attach click handlers to close buttons
      attachMentionBadgeHandlers(spanRef.current);
    }
    // Only run this effect on mount to set the initial value of the input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useUpdateValue;
